<script lang="ts" setup>
import { Jsona } from "jsona";
import type IJsonApiPageMeta from "~/interfaces/IJsonApiPageMeta";
import type Product from "~/models/Product";
import ProductReview, {
  type IProductReview,
  type IProductReviewState,
} from "~/models/ProductReview";
import ProductRating from "@/components/common/product/ProductRating.vue";
import auth from "~/services/AuthService";
import type { IJsonApiResponse } from "~/interfaces/IJsonApiResponse";
import { useIntersectionObserver } from "@vueuse/core";
import ProductReviewActionButton from "@/components/reviews/ProductReviewActionButton.vue";
import { parseError } from "@/utils/general";

const { $apiFetch } = useNuxtApp();

const props = defineProps<{ product: Product }>();

const jsona = new Jsona();
const ownReview = ref<ProductReview>();
const userLogged = computed(() => auth.isUserLogged());
const reviewState = ref<IProductReviewState>(ProductReview.getDefaultState());
const reviews = reactive<ProductReview[]>([]);
const isPageLoading = ref(false);
const initialized = ref(false);
const pageError = ref<string>();
const pageMeta = reactive({
  currentPage: 1,
  from: 0,
  lastPage: 1,
  perPage: 3,
  to: 0,
  total: 0,
} as IJsonApiPageMeta);

const hasMore = computed(() => pageMeta.currentPage < pageMeta.lastPage);
const visibleReviews = computed(() => {
  const items = ownReview.value
    ? [
        ownReview.value,
        ...reviews.filter((review) => review.id !== ownReview.value?.id),
      ]
    : reviews;
  return items;
});

const nextPage = async () => {
  if (hasMore.value) {
    pageMeta.currentPage++;
    await paginate();
  }
};

const paginate = async () => {
  const user = auth.getUser();
  if (!props.product.id || isPageLoading.value) {
    return;
  }
  isPageLoading.value = true;
  try {
    const path = `/shop-v1/products/${props.product.id}/reviews`;
    const queryStr = new URLSearchParams({
      "page[number]": pageMeta.currentPage + "",
      "page[size]": pageMeta.perPage + "",
      sort: "-id",
      ...ProductReview.getDefaultQueryFields(),
      ...(user?.id ? { "filter[-customer_id]": user.id } : undefined),
    } as Record<string, string>).toString();
    const doc = await $apiFetch<IJsonApiResponse>(path + "?" + queryStr);
    const jsonapi = jsona.deserialize(doc);
    const instances = ProductReview.fromArray(jsonapi as IProductReview[]);
    const meta = doc.meta as { page: IJsonApiPageMeta } | undefined;
    reviews.push(...instances);
    if (meta) {
      Object.assign(pageMeta, meta);
    }
  } catch (err) {
    pageError.value = await parseError(err);
  } finally {
    isPageLoading.value = false;
  }
};

const getReviewState = async () => {
  const user = auth.getUser();
  if (!user || !user.id) {
    return;
  }
  try {
    reviewState.value = await ProductReview.fetchStateByProduct(
      String(props.product.id),
    );
  } catch (err) {
    console.error(err);
  }
};

const getOwnReview = async () => {
  const user = auth.getUser();
  if (!user || !user.id) {
    return;
  }
  try {
    ownReview.value = await ProductReview.fetchOwnByProduct(
      String(props.product.id),
    );
  } catch (err) {
    console.error(err);
  }
};

const initialize = async () => {
  await Promise.all([paginate(), getReviewState(), getOwnReview()]);
  initialized.value = true;
};

const handleStateUpdated = (state: IProductReviewState) => {
  reviewState.value = { ...state };
};

const handleReviewUpdated = (review?: IProductReview) => {
  ownReview.value = review ? new ProductReview(review) : undefined;
};

const reviewsSection = ref<HTMLElement>();
let sectionIntersected = false;
useIntersectionObserver(reviewsSection, ([observer]) => {
  if (!sectionIntersected && observer?.isIntersecting) {
    sectionIntersected = true;
    initialize();
  }
});
</script>

<template>
  <div ref="reviewsSection">
    <div id="review"></div>
    <div id="product-review-section"></div>
    <div
      v-if="initialized && !ownReview && !reviews.length"
      class="alert alert-light"
      role="alert"
    >
      Todavía no hay reseñas para este producto, sé el primero en hacerlo.
    </div>
    <div
      v-if="initialized"
      class="mb-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2"
    >
      <div class="small text-secondary">
        <span v-if="!userLogged">Inicia sesión para enviar una reseña.</span>
        <span
          v-else-if="
            reviewState.reason === 'purchase_required' &&
            reviewState.requires_purchase_validation
          "
        >
          Este producto requiere una compra válida para reseñarse.
        </span>
        <span v-else-if="reviewState.requires_captcha">
          Tu reseña se valida con captcha antes de guardarse.
        </span>
      </div>
      <product-review-action-button
        v-if="props.product.id"
        :product-id="String(props.product.id)"
        :product-name="props.product.name || 'Producto'"
        :review-state="reviewState"
        @state-updated="handleStateUpdated"
        @review-updated="handleReviewUpdated"
      />
    </div>
    <p v-if="!initialized" class="placeholder-glow" aria-hidden="true">
      <span class="placeholder col-12 bg-light" style="height: 80px"></span>
    </p>
    <div
      v-for="review in visibleReviews"
      :key="review.id || undefined"
      class="card text-center mb-3"
      :class="{ shadow: ownReview?.id === review.id }"
    >
      <div
        class="card-header d-flex justify-content-between align-items-center position-relative"
      >
        <div class="small">
          <i class="bi bi-person"></i>
          {{ review.customer_name }}
        </div>
        <product-rating :rating="review.rating" />
        <span
          v-if="ownReview?.id === review.id"
          class="position-absolute top-0 start-50 translate-middle badge rounded-pill bg-secondary"
          >Tu reseña</span
        >
      </div>
      <div class="card-body">
        <div class="mb-2 d-flex justify-content-center gap-2 flex-wrap">
          <span
            v-if="review.verified_purchase"
            class="badge text-bg-light border"
          >
            Compra verificada
          </span>
          <span
            v-if="ownReview?.id === review.id && !review.is_approved"
            class="badge text-bg-warning"
          >
            En revisión
          </span>
        </div>
        <h5 class="card-title">
          {{ review.short_opinion }}
        </h5>
        <p class="card-text">
          <i class="bi bi-quote"></i>
          {{ review.opinion }}
        </p>
      </div>
      <div class="card-footer text-body-secondary text-start small">
        <i class="bi bi-clock"></i>
        {{ review.created_at_text }}
      </div>
    </div>
    <div v-if="pageError" class="alert alert-danger mb-3" role="alert">
      {{ pageError }}
    </div>
    <div
      v-if="(hasMore || (initialized && isPageLoading)) && reviews.length"
      class="text-center"
    >
      <button
        type="button"
        class="btn btn-outline-danger btn-sm"
        :disabled="isPageLoading"
        @click="nextPage()"
      >
        <i v-if="!isPageLoading" class="bi bi-arrow-down"></i>
        <span
          v-else
          class="spinner-border spinner-border-sm text-danger me-1"
          role="status"
        ></span>
        Cargar más reseñas
      </button>
    </div>
  </div>
</template>
